/**
 * User-selectable AI models, with the metadata the pickers need.
 *
 * Keep the IDs in sync with `supabase/functions/_shared/models.ts` — the edge
 * functions validate against their own allowlist, so an ID that drifts here
 * silently falls back to the default rather than erroring.
 *
 * Deliberately carries no supplier pricing. What a generation costs *us* is
 * internal; the user is billed in credits, and those live in the
 * generation_costs table so they can be changed without a deploy. Showing USD
 * here once leaked our cost price into the customer-facing picker.
 */

export interface ImageModelOption {
  id: string;
  label: string;
  blurb: string;
  /** Largest imageSize the model supports. */
  maxSize: "1K" | "4K";
}

export const IMAGE_MODEL_OPTIONS: ImageModelOption[] = [
  {
    id: "gemini-3.1-flash-image",
    label: "Flash",
    blurb: "Balanced — the default workhorse",
    maxSize: "4K",
  },
  {
    id: "gemini-3.1-flash-lite-image",
    label: "Flash Lite",
    blurb: "Fastest and cheapest · 1K only",
    maxSize: "1K",
  },
  {
    id: "gemini-3-pro-image",
    label: "Pro",
    blurb: "Highest quality, slower",
    maxSize: "4K",
  },
];

export const DEFAULT_IMAGE_MODEL = "gemini-3.1-flash-image";

/**
 * Models valid for a given target resolution. Format variants upscale to
 * 2K/4K, which rules out the 1K-only Lite model.
 */
export function imageModelsForSize(size: "1K" | "2K" | "4K"): ImageModelOption[] {
  return size === "1K"
    ? IMAGE_MODEL_OPTIONS
    : IMAGE_MODEL_OPTIONS.filter((m) => m.maxSize !== "1K");
}

export interface VideoModelOption {
  id: string;
  label: string;
  blurb: string;
  /** Action key in generation_costs, so the price shown is credits. */
  costKey: string;
}

export const VIDEO_MODEL_OPTIONS: VideoModelOption[] = [
  {
    id: "veo-3.1-lite-generate-preview",
    label: "Lite",
    blurb: "Cheapest",
    costKey: "video_lite",
  },
  {
    id: "veo-3.1-fast-generate-preview",
    label: "Fast",
    blurb: "Good balance",
    costKey: "video_fast",
  },
  {
    id: "veo-3.1-generate-preview",
    label: "Standard",
    blurb: "Highest quality",
    costKey: "video_standard",
  },
];

export const DEFAULT_VIDEO_MODEL = "veo-3.1-generate-preview";

/** Videos are a fixed 8 seconds, so tiers are directly comparable. */
export const VIDEO_DURATION_SECONDS = 8;
